// File: clientapp/src/pages/SecurityUsers.js
import React, { useEffect, useState } from 'react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Page, DetailRow, ExcelExport, Toolbar } from '@syncfusion/ej2-react-grids';
import {
    Container,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    TextField,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment
} from '@mui/material';
import { Link } from 'react-router-dom';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAppConfirm } from '../contexts/AppConfirmContext';
import { useAppSnackbar } from '../contexts/AppSnackbarContext';

function validatePassword(password) {
    return {
        length: password.length >= 6,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        digit: /\d/.test(password)
    };
}

export default function SecurityUsers() {
    const [filterText, setFilterText] = useState('');
    const { confirm } = useAppConfirm();
    const { showSnackbar } = useAppSnackbar();

    const [gridData, setGridData] = useState({ result: [], count: 0 });
    const [pageSettings, setPageSettings] = useState({
        pageSize: 20,
        currentPage: 1,
        totalRecordsCount: 0
    });
    const [filters, setFilters] = useState({});

    // New User Dialog state
    const [openNewUserDialog, setOpenNewUserDialog] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const [passwordValidations, setPasswordValidations] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        digit: false
    });

    let grid;

    useEffect(() => {
        fetchUsers();
    }, [pageSettings, filters]);

    useEffect(() => {
        setPasswordValidations(validatePassword(newUserPassword));
    }, [newUserPassword]);

    const parseUtcDate = (dateString) => {
        return new Date(dateString + 'Z'); // Appends Z = UTC
    };

    async function fetchUsers() {
        try {
            let url = `/api/security/users?page=${pageSettings.currentPage}&pageSize=${pageSettings.pageSize}`;
            if (Object.keys(filters || {}).length) {
                url += '&' + new URLSearchParams(filters).toString();
            }
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setGridData({
                    result: formatData(data.items),
                    count: data.total
                });
            }
        } catch (err) {
            console.error(err);
        }
    }

    const formatData = (data) => {
        return data.map((item) => ({
            ...item,
            lastLoginTimeFormatted: item.lastLoginTime ? new Date(item.lastLoginTime).toLocaleString('en-US') : null,
            lastLoginRaw: item.lastLoginTime ? parseUtcDate(item.lastLoginTime).toLocaleString('en-US') : null,
            lastLogin: item.lastLoginTime ? parseUtcDate(item.lastLoginTime).toLocaleString('en-US', {
                timeZoneName: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }) : 'Never',
            isLockedOut: item.lockoutEnd ? true : false,
            groupNames: item.groups ? item.groups.map(g => g.name).join(', ') : '',
        }));
    }

    const handleDelete = async (user) => {
        if (user.lockoutEnd) {
            showSnackbar('User is already locked.', 'error');
            return;
        }
        const confirmed = await confirm({
            title: 'Disable User',
            content: 'Are you sure you want to lock this user?',
            confirmText: 'Accept',
            cancelText: 'Cancel'
        });
        if (confirmed) {
            try {
                const response = await fetch(`/api/security/users/${user.id}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (response.ok) {
                    fetchUsers();
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleUnlock = async (id) => {
        try {
            await fetch(`/api/security/users/unlock/${id}`, {
                method: 'POST',
                credentials: 'include'
            });
            fetchUsers();
        } catch (err) {
            console.error(err);
        }
    };

    // New User Dialog handlers
    const handleOpenNewUserDialog = () => {
        setNewUserEmail('');
        setNewUserPassword('');
        setShowNewPassword(false);
        setDialogError('');
        setOpenNewUserDialog(true);
    };

    const handleCloseNewUserDialog = () => {
        setOpenNewUserDialog(false);
    };

    const handleCreateUser = async () => {
        setDialogError('');
        // Validate the new password
        const validations = validatePassword(newUserPassword);
        const allValid = Object.values(validations).every(v => v);
        if (!allValid) {
            setDialogError('Password does not meet complexity requirements.');
            return;
        }
        try {
            const response = await fetch('/api/security/users', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: newUserEmail, password: newUserPassword })
            });
            if (response.ok) {
                handleCloseNewUserDialog();
                fetchUsers();
            } else {
                const errorData = await response.json();
                setDialogError(errorData.message || 'Error creating user.');
            }
        } catch (err) {
            console.error(err);
            setDialogError('Error creating user.');
        }
    };

    const toolbarClick = async (args) => {
        if (grid && args.item.id === 'users-grid_excelexport') {
            try {
                let url = `/api/security/users?all=true`;
                url += '&' + new URLSearchParams(filters).toString();
                const response = await fetch(url, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                const result = await response.json();
                grid.showSpinner();

                const exportProps = {
                    exportType: 'AllPages',
                    dataSource: formatData(result.items),
                    columns: [
                        { field: 'id', headerText: 'Id' },
                        { field: 'email', headerText: 'Email' },
                        { field: 'encompassUsername', headerText: 'Encompass Username' },
                        { field: 'lastLoginTimeFormatted', headerText: 'Last Login (UTC)' },
                        { field: 'lastLoginRaw', headerText: 'Last Login (Local Time)' },
                        { field: 'status', headerText: 'Status' },
                        { field: 'groupNames', headerText: 'Groups' }
                    ]
                }

                grid.excelExport(exportProps);
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                grid.hideSpinner();
            }
        }
    }

    const excelExportComplete = () => {
        grid.hideSpinner();
    }

    const handleActionBegin = (args) => {
        if (args.requestType === 'paging') {
            setPageSettings(prev => ({
                ...prev,
                currentPage: args.currentPage
            }));
        }
    };

    useEffect(() => {
        const id = setTimeout(() => {
            setPageSettings(prev => ({ ...prev, currentPage: 1 }));
            setFilters(prev => {
                const next = { ...prev };
                if (filterText) next.email = filterText;
                else delete next.email;
                return next;
            });
        }, 1000);
        return () => clearTimeout(id);
    }, [filterText]);

    const handleFilterChange = (e) => {
        setFilterText(e.target.value);
    }

    return (
        <Container>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Manage Users</Typography>
                <Tooltip title="Create New User">
                    <IconButton color="primary" onClick={handleOpenNewUserDialog}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box mb={2}>
                <TextField
                    label="Search by Email"
                    variant="outlined"
                    size="small"
                    value={filterText}
                    onChange={handleFilterChange}
                    fullWidth
                />
            </Box>
            <Paper>
                <GridComponent
                    id='users-grid'
                    ref={g => grid = g}
                    dataSource={gridData}
                    pageSettings={pageSettings}
                    actionBegin={handleActionBegin}
                    height='60vh'
                    toolbar={['ExcelExport']}
                    toolbarClick={toolbarClick}
                    excelExportComplete={excelExportComplete}
                    allowPaging
                    allowExcelExport
                >
                    <ColumnsDirective>
                        <ColumnDirective field='email' headerText='Email' />
                        <ColumnDirective field='lastLogin' headerText='Last Login' />
                        <ColumnDirective field='status' headerText='Status' />
                        <ColumnDirective headerText='Actions' template={(props) => (
                            <>
                                <Tooltip title="Lock User">
                                    <IconButton size="small" onClick={() => handleDelete(props)}>
                                        <LockIcon />
                                    </IconButton>
                                </Tooltip>
                                {props.isLockedOut && (
                                    <Tooltip title="Unlock User">
                                        <IconButton size="small" onClick={() => handleUnlock(props.id)}>
                                            <LockOpenIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        )} />

                        <ColumnDirective headerText='Detail' template={(props) => (
                            <Tooltip title="View Details / Edit">
                                <IconButton size="small" component={Link} to={`/security/users/${props.id}`}>
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                        )} />
                    </ColumnsDirective>
                    <Inject services={[Page, DetailRow, ExcelExport, Toolbar]} />
                </GridComponent>
            </Paper>

            {/* New User Dialog */}
            <Dialog open={openNewUserDialog} onClose={handleCloseNewUserDialog} fullWidth maxWidth="sm">
                <DialogTitle>Create New User</DialogTitle>
                <DialogContent>
                    {dialogError && (
                        <Typography variant="body2" color="error" gutterBottom>
                            {dialogError}
                        </Typography>
                    )}
                    <TextField
                        margin="dense"
                        label="Email"
                        type="email"
                        fullWidth
                        value={newUserEmail}
                        onChange={e => setNewUserEmail(e.target.value)}
                    />
                    <FormControl fullWidth margin="dense" variant="outlined">
                        <InputLabel htmlFor="new-user-password">Password</InputLabel>
                        <OutlinedInput
                            id="new-user-password"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newUserPassword}
                            onChange={e => setNewUserPassword(e.target.value)}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Password"
                        />
                    </FormControl>
                    <Box mt={2}>
                        <Typography variant="body2">Password must meet the following requirements:</Typography>
                        <Box ml={2}>
                            <Typography variant="caption" color={passwordValidations.length ? 'green' : 'red'}>
                                • At least 6 characters
                            </Typography><br />
                            <Typography variant="caption" color={passwordValidations.uppercase ? 'green' : 'red'}>
                                • At least one uppercase letter
                            </Typography><br />
                            <Typography variant="caption" color={passwordValidations.lowercase ? 'green' : 'red'}>
                                • At least one lowercase letter
                            </Typography><br />
                            <Typography variant="caption" color={passwordValidations.digit ? 'green' : 'red'}>
                                • At least one digit
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNewUserDialog}>Cancel</Button>
                    <Button onClick={handleCreateUser} variant="contained" color="primary">
                        Create User
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
